export const LAYOUT_KIND_CHART = 'chart';
export const LAYOUT_KIND_TEXT = 'text';

export const GRID_COLS_LG = 12;

function newItemId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `item-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** @param {object} item */
export function isTextLayoutItem(item) {
    return item?.kind === LAYOUT_KIND_TEXT;
}

/** @param {object} item */
export function isChartLayoutItem(item) {
    return !item?.kind || item.kind === LAYOUT_KIND_CHART;
}

/** @param {object} [layoutJson] */
export function parseLayoutItems(layoutJson) {
    return (layoutJson?.charts ?? []).map(item => (
        isTextLayoutItem(item) ? item : { ...item, kind: LAYOUT_KIND_CHART }
    ));
}

export function createTextBlock({ title, description }) {
    return {
        id: newItemId(),
        kind: LAYOUT_KIND_TEXT,
        title: (title || '').trim(),
        description: (description || '').trim() || null,
        layout: { x: 0, y: Infinity, w: GRID_COLS_LG, h: 4 },
    };
}

export function cloneTextBlock(item, options = {}) {
    const { titleSuffix = ' (copy)', layoutDelta = { x: 0, y: 1 } } = options;
    const layout = item.layout ?? { x: 0, y: 0, w: GRID_COLS_LG, h: 4 };
    return {
        ...item,
        id: newItemId(),
        kind: LAYOUT_KIND_TEXT,
        title: item.title ? `${item.title}${titleSuffix}` : 'Text',
        layout: {
            ...layout,
            w: GRID_COLS_LG,
            x: 0,
            y: (layout.y ?? 0) + layoutDelta.y,
        },
    };
}

/** react-grid-layout item props per layout kind */
export function toGridLayoutItem(item) {
    const base = {
        i: item.id,
        x: item.layout?.x ?? 0,
        y: item.layout?.y ?? 0,
        w: item.layout?.w ?? 6,
        h: item.layout?.h ?? 10,
    };
    if (isTextLayoutItem(item)) {
        return {
            ...base,
            w: GRID_COLS_LG,
            minW: GRID_COLS_LG,
            maxW: GRID_COLS_LG,
            minH: 2,
            maxH: 12,
        };
    }
    if (item?.viz?.type === 'stat') {
        return { ...base, minW: 2, minH: 4, maxW: 6 };
    }
    return { ...base, minW: 3, minH: 6 };
}

export function countChartItems(items = []) {
    return items.filter(isChartLayoutItem).length;
}

/**
 * Apply react-grid-layout positions onto dashboard layout items.
 * @param {object[]} items
 * @param {Array<{ i: string, x: number, y: number, w: number, h: number }>} gridLayout
 */
export function mergeGridLayoutIntoItems(items, gridLayout) {
    return items.map(item => {
        const pos = gridLayout.find(l => l.i === item.id);
        if (!pos) return item;
        const layout = { x: pos.x, y: pos.y, w: pos.w, h: pos.h };
        if (isTextLayoutItem(item)) {
            return { ...item, layout: { ...layout, w: GRID_COLS_LG, x: 0 } };
        }
        return { ...item, layout };
    });
}

/** Compare react-grid-layout position arrays (ignore transient RGL fields). */
export function gridLayoutsEqual(a, b) {
    if (a === b) return true;
    if (!a?.length || !b?.length || a.length !== b.length) return false;
    const byId = new Map(b.map(item => [item.i, item]));
    return a.every(item => {
        const other = byId.get(item.i);
        if (!other) return false;
        return item.x === other.x
            && item.y === other.y
            && item.w === other.w
            && item.h === other.h;
    });
}

/** Shallow compare layout items (id + layout positions + chart config refs). */
export function layoutItemsEqual(a, b) {
    if (a === b) return true;
    if (!a?.length || !b?.length || a.length !== b.length) return false;
    return a.every((item, i) => {
        const other = b[i];
        if (item.id !== other.id) return false;
        const la = item.layout ?? {};
        const lb = other.layout ?? {};
        return la.x === lb.x && la.y === lb.y && la.w === lb.w && la.h === lb.h
            && item.functionId === other.functionId
            && item.dimensionFilter === other.dimensionFilter
            && item.title === other.title
            && JSON.stringify(item.viz) === JSON.stringify(other.viz);
    });
}

export const GRID_DRAG_HANDLE_CLASS = 'drag-handle';
export const GRID_DRAG_CANCEL_CLASS = 'no-drag';

/** Legacy react-grid-layout props — drag only from the header grip (`.drag-handle`). */
export function gridLayoutDragProps(editable) {
    if (!editable) {
        return { isDraggable: false, isResizable: false };
    }
    return {
        isDraggable: true,
        isResizable: true,
        draggableHandle: `.${GRID_DRAG_HANDLE_CLASS}`,
        draggableCancel: `.${GRID_DRAG_CANCEL_CLASS}`,
    };
}
